import {Alert, Spin} from "@ui";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ConfEnv} from "../../../config/config";
import {useBetbyWidgetManager} from "../../../hooks/useBetbyWidgetManager";
import useGetTokenBetby from "../../../hooks/useGetTokenBetby";
import useBetbyStore from "../../../store/Betby/useBetbyStore.store";
import {useAuthStore} from "../../../store/useAuth.store";

const BetbyWidgetPromo = () => {
  const {t} = useTranslation();
  const lang = "es";

  // Extract currency from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const currency = urlParams.get("currency") || "VES"; // Default to VES if no currency specified
  const authStatus = useAuthStore(state => state.authStatus);
  const tokenBetby = useBetbyStore(state => state.tokenBetby);

  // Use the hook instead of calling service directly
  const {isLoading: isTokenLoading, refetch: refetchToken} = useGetTokenBetby(
    lang,
    currency,
    authStatus === "authenticated"
  );

  // Stable callback handlers to prevent unnecessary re-renders
  const handleBannerClick = useCallback(args => {
    window.location.assign(`/sports?page=${args.url}`);
  }, []);

  const handleOutcomeClick = useCallback(args => {
    window.location.assign(`/sports?bt-path=${args.url}`);
  }, []);

  const handleGoToClick = useCallback(args => {
    window.location.assign(`/sports?bt-path=${args.url}`);
  }, []);

  // Create widget configuration with stable dependencies
  const widgetConfig = useMemo(() => {
    if (!tokenBetby || authStatus !== "authenticated" || isTokenLoading) {
      return null;
    }

    return {
      token: tokenBetby,
      brand_id: ConfEnv.API_BETBY.BRAND_ID,
      lang: lang,
      themeName: ConfEnv.API_BETBY.THEME,
      widgetName: "promo",
      widgetParams: {
        placeholder: "operator_page1",
        onBannerClick: handleBannerClick,
        onOutcomeClick: handleOutcomeClick,
        goToClick: handleGoToClick,
        onError: _error => {},
      },
    };
  }, [tokenBetby, authStatus, isTokenLoading, lang, handleBannerClick, handleOutcomeClick, handleGoToClick]);

  // Use the Betby widget manager
  const {
    containerRef,
    isLoading: isWidgetLoading,
    error,
    retryInitialization,
    setError,
  } = useBetbyWidgetManager(widgetConfig);

  // The component shouldn't render anything if the user is not authenticated
  if (authStatus !== "authenticated") {
    return null;
  }

  // Handle case where user is authenticated but token is not available
  if (authStatus === "authenticated" && !isTokenLoading && !tokenBetby) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "400px",
          padding: "8px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <Alert
          message={t("tokenError", "Sports Token Error")}
          description={
            <div
              style={{
                color: "var(--theme-text-on-primary)",
                textAlign: "center",
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              <p
                style={{
                  marginBottom: "16px",
                  color: "var(--theme-text-on-primary)",
                  fontSize: "14px",
                  lineHeight: "1.4",
                }}
              >
                {t("tokenErrorDesc", "Unable to get sports betting token. This is required to load sports content.")}
              </p>
              <div style={{display: "flex", justifyContent: "center"}}>
                <button
                  type="button"
                  onClick={() => {
                    refetchToken();
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "var(--color-accent-blue)",
                    color: "var(--text-primary)",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {t("retry", "Retry")}
                </button>
              </div>
            </div>
          }
          type="warning"
          showIcon
          style={{
            marginBottom: "16px",
            width: "100%",
            boxSizing: "border-box",
            wordWrap: "break-word",
            overflow: "hidden",
          }}
        />
      </div>
    );
  }

  const isLoading = isTokenLoading || isWidgetLoading;

  return (
    <div style={{width: "100%", minHeight: "400px"}}>
      {isLoading && (
        <Spin
          size="large"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "20px 0",
          }}
        />
      )}

      {error && (
        <Alert
          message={t("error")}
          description={error}
          type="error"
          showIcon
          style={{marginBottom: "16px"}}
          closable
          onClose={() => setError(null)}
          action={
            <button
              type="button"
              onClick={retryInitialization}
              style={{
                padding: "8px 16px",
                backgroundcolor: "var(--status-error)",
                color: "var(--text-primary)",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              {t("retry", "Retry")}
            </button>
          }
        />
      )}

      <div
        ref={containerRef}
        style={{width: "100%", minHeight: "400px"}}
      />
    </div>
  );
};

export default BetbyWidgetPromo;
